import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FaCheckCircle, FaExclamationTriangle, FaImage } from 'react-icons/fa';
import { FileUploadInput } from 'src/components/Form/upload-input';
import { CreateRewardFormData } from '../../utils/reward.validation';

interface RewardMediaFieldsProps {
  isEditMode: boolean;
  initialDataAttachmentUrl?: string;
}

export const RewardMediaFields: React.FC<RewardMediaFieldsProps> = ({
  isEditMode,
  initialDataAttachmentUrl,
}) => {
  const {
    control,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useFormContext<CreateRewardFormData>();

  const mediaFiles = watch('mediaFiles');
  const providerName = watch('providerName');
  const tremendousProduct = watch('tremendousProduct');
  const isTremendousReward = providerName === 'TREMENDOUS';
  const previewImage = tremendousProduct?.imageUrl || initialDataAttachmentUrl;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100 space-y-6">
      <div className="border-b border-gray-100 pb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Step 3</p>
        <h3 className="mt-2 text-xl font-bold text-gray-800 flex items-center gap-2">
          <FaImage className="text-indigo-500" /> Reward Media
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Add the image users will see for this reward, or let the provider sync it automatically.
        </p>
      </div>

      <div className="space-y-4">
        {isTremendousReward ? (
          <div className="rounded-2xl border border-fuchsia-100 bg-fuchsia-50/70 p-4">
            {previewImage ? (
              <div className="flex items-center gap-3">
                <img
                  src={previewImage}
                  alt={tremendousProduct?.name || 'Tremendous product'}
                  className="w-16 h-16 rounded-lg object-cover border border-fuchsia-100"
                />
                <div className="text-sm">
                  <p className="font-medium text-gray-700">Default image loaded from Tremendous</p>
                  <p className="text-gray-500 text-xs">You can keep it or upload a different image below.</p>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                <p className="font-medium text-gray-700">No image was returned for this Tremendous product.</p>
                <p className="mt-1 text-xs">
                  Upload an image below if you want to add one for this reward.
                </p>
              </div>
            )}
          </div>
        ) : (
          isEditMode && initialDataAttachmentUrl && mediaFiles?.length === 0 && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <img src={initialDataAttachmentUrl} alt="Current" className="w-16 h-16 rounded-lg object-cover" />
              <div className="text-sm">
                <p className="font-medium text-gray-700">Current media</p>
                <p className="text-gray-400 text-xs">Upload a new file below only if you want to replace it.</p>
              </div>
            </div>
          )
        )}

        <Controller
          name="mediaFiles"
          control={control}
          render={({ field }) => (
            <FileUploadInput
              handleInputFile={(files) => {
                field.onChange(files);
                trigger('mediaFiles');
              }}
              reset={isValid && field.value?.length === 0 && !isEditMode}
              maxFiles={1}
            />
          )}
        />

        {errors.mediaFiles ? (
          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
            <FaExclamationTriangle /> {errors.mediaFiles.message as string}
          </p>
        ) : mediaFiles?.length > 0 ? (
          <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
            <FaCheckCircle /> Media ready to upload.
          </p>
        ) : isTremendousReward ? (
          <p className="text-xs text-gray-500">
            If you do not upload a file, the Tremendous product image will be used by default.
          </p>
        ) : null}
      </div>
    </div>
  );
};
